/* Imports */
import { defineStore } from 'pinia';
import {
  request,
  getApiUrl,
  sortProductData,
} from '../helpers/mainHelpers.js';
import { useContentfulFeatureFlagsStore } from './contentfulFeatureFlags.js';
import { useFlashMessagesStore } from './flashMessages.js';

export const useGlobalStore = defineStore('global', {
  state: () => ({
    locale: '',
    languageCode: '',
    countryCode: '',
    translations: {},
    productData: {},
    noProducts: false,
    showDifferences: false,
    subdirectory: '',
    pricingProxyServicesBaseUrl: '',
  }),
  getters: {
    getLocale: (state) => state.locale,
    getLanguageCode: (state) => state.languageCode,
    getCountryCode: (state) => state.countryCode,
    getTranslations: (state) => state.translations,
    getPricingProxyServicesBaseUrl: (state) => state.pricingProxyServicesBaseUrl,
    getProductData: (state) => state.productData,
    getShowDifferences: (state) => state.showDifferences,
    getSubdirectory: (state) => state.subdirectory,
  },
  actions: {
    setLocale(payload) {
      this.locale = payload;
    },
    setTranslations(payload) {
      this.translations = payload;
    },
    setSubdirectory(payload) {
      this.subdirectory = payload;
    },
    setLanguageCode(payload) {
      this.languageCode = payload;
    },
    setCountryCode(payload) {
      this.countryCode = payload;
    },
    setProductData(payload) {
      const queryParams = new URLSearchParams(window?.location?.search);
      const compareProductsQueryParams = queryParams.getAll('compareProduct');
      if (payload?.products) {
        this.productData = sortProductData(payload, compareProductsQueryParams);
      } else {
        this.productData = payload;
      }
    },
    setPricingProxyServicesBaseUrl(payload) {
      this.pricingProxyServicesBaseUrl = payload;
    },
    setInitialState() {
      const contentfulFeatureFlagsStore = useContentfulFeatureFlagsStore();
      const flashMessagesStore = useFlashMessagesStore();

      const {
        translations = {},
        locale,
        languageCode,
        countryCode,
        subdirectory,
        pricingProxyServicesBaseUrl,
        productData,
        envSettings,
      } = window.AppData || {};

      this.setTranslations(translations);
      this.setLocale(locale);
      this.setLanguageCode(languageCode);
      this.setCountryCode(countryCode);
      this.setSubdirectory(subdirectory);
      this.setPricingProxyServicesBaseUrl(pricingProxyServicesBaseUrl);
      this.setProductData(productData);
      contentfulFeatureFlagsStore.setContentfulEnvSettings(envSettings);

      // Inject error flash message if compare API failed
      if (productData?.status === 'error') {
        flashMessagesStore.setFlashMessage({
          type: 'error',
          text: this.getTranslations?.ITFE_PRODUCT_COMPARE_COMPARE_API_ERROR_MESSAGE,
          closeable: false,
          testId: 'product-compare-unavailable',
          align: 'center',
        });
      }
    },
    setShowDifferences(payload) {
      this.showDifferences = payload;
    },
    async getProductDataByPids(pids) {
      const localeParts = this.locale.split('-');
      const pIdParams = pids.map((pId) => `&pId=${pId}`);
      const url = `${getApiUrl()}/getProductDataByPids`
        + `?locale=${this.locale}`
        + `&countryCode=${localeParts[1]}`
        + `${pIdParams.join('')}`;

      try {
        const response = await request({
          method: 'GET',
          url,
        });

        return response;
      } catch (err) {
        const flashMessagesStore = useFlashMessagesStore();

        flashMessagesStore.setFlashMessage({
          type: 'error',
          text: this.getTranslations?.ITFE_PRODUCT_COMPARE_COMPARE_API_ERROR_MESSAGE,
          closeable: false,
          testId: 'product-data-by-pids',
          align: 'center',
        });

        throw err;
      }
    },
    async removeProduct(payload) {
      const queryParams = { ...this.router.currentRoute.value?.query };

      const compareProducts = Array.isArray(queryParams.compareProduct)
        ? queryParams.compareProduct
        : [queryParams.compareProduct];

      const productIndex = this.productData.products.findIndex((product) => product.pid === payload);
      if (productIndex === -1) return;

      // Build a list of remaining product IDs (excluding the one being removed)
      const remainingPids = this.productData?.products
        ?.filter((product) => product?.pid !== payload)
        ?.map((product) => product?.pid);

      if (remainingPids?.length) {
        // Updates the specs data for the remaining pids
        const data = await this.getProductDataByPids(remainingPids);
        this.setProductData(data);
        queryParams.compareProduct = compareProducts?.filter((pid) => pid !== payload);
      } else {
        // No products - reset data
        this.productData = {
          products: [],
          productSpecs: {},
        };
        this.noProducts = true;
        delete queryParams.compareProduct;
      }

      this.router.push({ query: queryParams });
    },
    async addProduct(newPid) {
      // 1. Get current query params
      const queryParams = { ...this.router.currentRoute.value?.query };

      // 2. Normalize compareProducts
      let compareProducts;

      if (Array.isArray(queryParams.compareProduct)) {
        compareProducts = [...queryParams.compareProduct];
      } else if (queryParams.compareProduct) {
        compareProducts = [queryParams.compareProduct];
      } else {
        compareProducts = [];
      }

      // 3. Avoid duplicates
      if (compareProducts.includes(newPid)) return;

      // 4. Add new PID at the end
      compareProducts.push(newPid);

      // 5. Fetch updated data for all products in compareProducts
      const data = await this.getProductDataByPids(compareProducts);

      // 6. Update store: reorder products and merge specs
      this.productData = {
        ...this.productData,
        ...data,
        products: sortProductData(data, compareProducts).products,
        productSpecs: sortProductData(data, compareProducts).productSpecs,
      };

      // 7. Update query params
      this.router.push({ query: { ...queryParams, compareProduct: compareProducts } });

      // 8. Set no products state
      this.noProducts = !this.productData?.products?.length;
    },
  },
});

export default useGlobalStore;
